import { useState, useEffect, useMemo, useCallback, MouseEvent } from 'react';
import { ReportElementUnion, DataTableElement, StatisticsSummaryElement, QQPlotElement, GraphBuilderElement, CrosstabElement } from '../../types/report.types';
import { DataSet, DerivedDataSet, hasData, DatasetWithData } from '../../types/dataset.types';
import { createColumnName } from '../../types';
import type { TrainedModel } from '../../types/model.types';
import { ROW_ID_COLUMN_ID, ROW_ID_COLUMN_NAME } from '../../types/system-columns';
import { createColumnId } from '../../types';
import { QQPlot } from './visualization/QQPlot';
import { EditableTitle } from './EditableTitle';
import { getColumnData, getColumnNameById } from '../utils/column-utils';
import { graphRegistry } from './graph-builder/GraphRegistry';
import { evaluateDerivedDataset } from '../utils/evaluateDerivedDataset';
import { useDatasetSelection } from '../stores/hooks/useDatasetSelection';
import { applyFilterToDataset } from '../utils/applyFilterToDataset';
import { calculateStats } from '../utils/StatisticsCalculator';
import { StatisticsDisplay } from './statistics/StatisticsDisplay';
import logger from '../utils/logger';
import * as styles from '../styles/components/report-element-renderer.css.ts';
import * as columnStatsStyles from '../styles/components/statistics/column-statistics.css';
import { CrosstabPreview } from './TabContainer/tabs/components/CrosstabPreview';
import type { CrosstabDataModel } from '../utils/crosstab/types';
import { CrosstabEngine } from '../utils/crosstab/engine';
import { REPORT_ELEMENT_SIZING } from '../constants/reportElementSizing';

// D3グラフのデフォルト高さ設定
const DEFAULT_HISTOGRAM_HEIGHT = 350;
const DEFAULT_SCATTER_HEIGHT = 450;

/**
 * ReportElementRenderer - レポート要素レンダラーコンポーネント
 * 
 * 機能:
 * - 各タイプのレポート要素を適切にレンダリング
 * - データテーブル、グラフ、ヒストグラム、テキストメモ、統計サマリーに対応
 */

interface ReportElementRendererProps {
  element: ReportElementUnion;
  datasets: Record<string, DataSet>;
  models?: Record<string, TrainedModel>;
  onElementUpdate?: (elementId: string, updates: Partial<ReportElementUnion>) => void;
  width?: 'fit' | number;
  height?: number;
}

export function ReportElementRenderer({ element, datasets, models, onElementUpdate, width: _width, height }: ReportElementRendererProps) {
  switch (element.type) {
    case 'data_table':
      return <DataTableElementRenderer element={element} datasets={datasets} models={models} />;
    case 'statistics_summary':
      return <StatisticsSummaryElementRenderer element={element} datasets={datasets} models={models} />;
    case 'qq_plot':
      return <QQPlotElementRenderer element={element} datasets={datasets} models={models} />;
    case 'graph_builder':
      return <GraphBuilderElementRenderer element={element} datasets={datasets} models={models} onElementUpdate={onElementUpdate} height={height} />;
    case 'crosstab':
      return <CrosstabElementRenderer element={element} datasets={datasets} models={models} onElementUpdate={onElementUpdate} height={height} />;
    default:
      return (
        <div className={styles.errorMessage}>
          <p>Unsupported element type: {element.type}</p>
        </div>
      );
  }
}

// データテーブル要素レンダラー
function DataTableElementRenderer({ element, datasets, models }: { element: DataTableElement; datasets: Record<string, DataSet>; models?: Record<string, TrainedModel> }) {
  const { datasetId, displayColumns, maxRows, formatting } = element;
  const dataset = datasets[datasetId];
  const [evaluatedData, setEvaluatedData] = useState<Record<string, unknown[]> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 派生データセットの評価
  useEffect(() => {
    if (dataset?.type === 'derived' && !dataset.data) {
      setIsLoading(true);
      setError(null);

      evaluateDerivedDataset(dataset as DerivedDataSet, datasets)
        .then(result => {
          setEvaluatedData(result.data);
          setIsLoading(false);
        })
        .catch(err => {
          logger.error('[ReportElementRenderer] Failed to evaluate derived dataset:', err);
          setError(err.message || 'Failed to evaluate dataset');
          setIsLoading(false);
        });
    }
  }, [dataset, datasets, models]);
  
  if (!dataset) {
    return (
      <div className={styles.reportDataTable}>
        <div className={styles.errorMessage}>
          <p>Dataset not found: {datasetId}</p>
        </div>
      </div>
    );
  }
  
  // 派生データセットのローディング中
  if (dataset.type === 'derived' && isLoading) {
    return (
      <div className={styles.reportDataTable}>
        <div className={styles.errorMessage}>
          <p>Evaluating derived dataset...</p>
        </div>
      </div>
    );
  }
  
  // 派生データセットの評価エラー
  if (dataset.type === 'derived' && error) {
    return (
      <div className={styles.reportDataTable}>
        <div className={styles.errorMessage}>
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }
  
  // 表示する行データを制限
  const rowCount = dataset.rowCount || 0;
  const displayRowCount = Math.min(maxRows, rowCount);
  
  // データソースを取得（PrimaryとDerivedの両方に対応）
  const dataSource = dataset.type === 'derived' 
    ? (evaluatedData || dataset.data as Record<string, unknown[]>)
    : dataset.data as Record<string, unknown[]>;
  
  if (!dataSource) {
    return (
      <div className={styles.reportDataTable}>
        <div className={styles.errorMessage}>
          <p>No data available</p>
        </div>
      </div>
    );
  }
  
  // displayColumnsをカラムIDとカラム名のペアに変換
  // displayColumnsには「カラム名」が入っている（ColumnName型）
  // ROW_ID_COLUMN_NAMEは除外する（行番号列として別途表示するため）
  const columnsToDisplay = displayColumns
    .filter(columnName => columnName !== ROW_ID_COLUMN_NAME)
    .map(columnName => {
      // カラム名からカラム情報を取得
      const column = dataset.schema.columns.find(col => col.name === columnName);
      if (column) {
        return { id: column.id, name: column.name };
      }

      // 見つからない場合はそのまま表示（エラー防止）
      logger.warn('[ReportElementRenderer] Column not found:', columnName);
      return { id: createColumnId(columnName), name: columnName };
    });
  
  return (
    <div className={styles.reportDataTable}>
      <div className={styles.tableContainer}>
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {/* 行番号列のヘッダー */}
              <th
                style={{
                  ...formatting.headerStyle,
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  zIndex: 2,
                  width: '60px',
                  minWidth: '60px',
                  maxWidth: '60px',
                  backgroundColor: '#f8f9fa',
                  borderRight: '1px solid #dee2e6',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}
              >
                #
              </th>
              {columnsToDisplay.map((column) => (
                <th
                  key={column.id}
                  style={{
                    ...formatting.headerStyle,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    minWidth: '120px',
                    padding: '8px 12px'
                  }}
                >
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: displayRowCount }, (_, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  backgroundColor: rowIndex % 2 === 1 ? formatting.alternateRowColor : 'transparent'
                }}
              >
                {/* 行番号列 */}
                <td
                  style={{
                    ...formatting.cellStyle,
                    position: 'sticky',
                    left: 0,
                    width: '60px',
                    minWidth: '60px',
                    maxWidth: '60px',
                    backgroundColor: rowIndex % 2 === 1 ? '#f5f5f5' : '#fafafa',
                    borderRight: '1px solid #dee2e6',
                    textAlign: 'center',
                    fontWeight: 'normal',
                    color: '#6c757d'
                  }}
                >
                  {(() => {
                    // ROW_ID_COLUMN_IDの値を取得（通常は1から始まる）
                    const rowIdColumnData = dataSource[ROW_ID_COLUMN_ID];
                    if (rowIdColumnData && rowIdColumnData[rowIndex] !== undefined) {
                      // Row #列の値をそのまま使用（通常1から始まる）
                      const rowId = rowIdColumnData[rowIndex];
                      // 数値の場合は+1する（0ベースの場合の対応）
                      if (typeof rowId === 'number' && rowId === rowIndex) {
                        return String(rowId + 1);
                      }
                      return String(rowId);
                    }
                    // フォールバック: ROW_ID列がない場合は1から始まるインデックス
                    return String(rowIndex + 1);
                  })()}
                </td>
                {columnsToDisplay.map((column) => {
                  const value = dataSource[column.id]?.[rowIndex];
                  return (
                    <td
                      key={`${rowIndex}-${column.id}`}
                      style={{
                        ...formatting.cellStyle,
                        minWidth: '120px',
                        padding: '8px 12px'
                      }}
                    >
                      {value !== null && value !== undefined ? String(value) : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rowCount > maxRows && (
        <div className={styles.tableInfo} style={{ padding: '8px', fontSize: '0.9em', color: '#666' }}>
          {maxRows.toLocaleString()} / {rowCount.toLocaleString()} rows displayed
        </div>
      )}
    </div>
  );
}

// 統計サマリー要素レンダラー
function StatisticsSummaryElementRenderer({ element, datasets, models: _models }: { element: StatisticsSummaryElement; datasets: Record<string, DataSet>; models?: Record<string, TrainedModel> }) {
  const { datasetId, targetColumns } = element;
  const dataset = datasets[datasetId];
  const [evaluatedData, setEvaluatedData] = useState<Record<string, unknown[]> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 選択状態管理
  const { selectedRows, selectRows } = useDatasetSelection(datasetId);

  // 派生データセットの評価
  useEffect(() => {
    if (dataset?.type === 'derived' && !dataset.data) {
      setIsLoading(true);
      setError(null);

      evaluateDerivedDataset(dataset as DerivedDataSet, datasets)
        .then(result => {
          setEvaluatedData(result.data);
          setIsLoading(false);
        })
        .catch(err => {
          logger.error('[StatisticsSummaryElementRenderer] Failed to evaluate derived dataset:', err);
          setError(err.message || 'Failed to evaluate dataset');
          setIsLoading(false);
        });
    }
  }, [dataset, datasets]);

  // カテゴリクリックハンドラー
  const handleCategoryClick = useCallback((indices: number[], event: MouseEvent<HTMLSpanElement>) => {
    if (indices.length === 0) return;

    const isAdditive = event.metaKey || event.ctrlKey;

    if (isAdditive) {
      // 追加選択モード
      const allSelected = indices.every(idx => selectedRows.includes(idx));
      if (allSelected) {
        // 全選択済み → 解除
        const newSelection = selectedRows.filter(idx => !indices.includes(idx));
        selectRows(newSelection);
      } else {
        // 未選択 → 追加
        const newSelection = [...new Set([...selectedRows, ...indices])];
        selectRows(newSelection);
      }
    } else {
      // 置換モード
      selectRows(indices);
    }
  }, [selectRows, selectedRows]);

  // データセットが見つからない
  if (!dataset) {
    return (
      <div className={styles.errorMessage} style={{ minHeight: `${REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT}px` }}>
        <p>Dataset not found: {datasetId}</p>
      </div>
    );
  }

  // 派生データセットのローディング中
  if (dataset.type === 'derived' && isLoading) {
    return (
      <div className={styles.errorMessage} style={{ minHeight: `${REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT}px` }}>
        <p>Evaluating derived dataset...</p>
      </div>
    );
  }

  // 派生データセットの評価エラー
  if (dataset.type === 'derived' && error) {
    return (
      <div className={styles.errorMessage} style={{ minHeight: `${REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT}px` }}>
        <p>Error: {error}</p>
      </div>
    );
  }

  // 統計計算用のデータセットを取得
  const dataSource = dataset.type === 'derived'
    ? (evaluatedData || dataset.data as Record<string, unknown[]>)
    : dataset.data as Record<string, unknown[]>;

  if (!dataSource) {
    return (
      <div className={styles.errorMessage} style={{ minHeight: `${REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT}px` }}>
        <p>No data available</p>
      </div>
    );
  }

  // DatasetWithData型を作成（hasDataでデータ存在を確認済みなので型アサーションは安全）
  const datasetForStats = {
    ...dataset,
    data: dataSource
  } as DatasetWithData;

  return (
    <div className={styles.reportStatisticsSummary} style={{ minHeight: `${REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT}px` }}>
      {targetColumns.map(columnName => {
        const stats = calculateStats(datasetForStats, createColumnName(columnName));
        if (!stats) {
          return (
            <div key={columnName} className={columnStatsStyles.columnStats}>
              <h4 className={columnStatsStyles.columnStatsTitle}>{columnName}</h4>
              <p className={styles.errorMessage}>Failed to calculate statistics for this column</p>
            </div>
          );
        }

        return (
          <div key={columnName} className={columnStatsStyles.columnStats}>
            <h4 className={columnStatsStyles.columnStatsTitle}>{columnName}</h4>
            <StatisticsDisplay
              stats={stats}
              onCategoryClick={handleCategoryClick}
            />
          </div>
        );
      })}
    </div>
  );
}

// QQプロット要素レンダラー
function QQPlotElementRenderer({ element, datasets, models: _models }: { element: QQPlotElement; datasets: Record<string, DataSet>; models?: Record<string, TrainedModel> }) {
  const { datasetId, columnId, styling } = element;
  const dataset = datasets[datasetId];
  
  if (!dataset) {
    return (
      <div className={styles.errorMessage} style={{ minHeight: `${REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT}px` }}>
        <p>データセットが見つかりません: {datasetId}</p>
      </div>
    );
  }

  // データを取得
  const columnData = getColumnData(dataset, columnId);
  const columnName = getColumnNameById(dataset, columnId) || columnId;
  
  if (!columnData) {
    return (
      <div className={styles.errorMessage} style={{ minHeight: `${REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT}px` }}>
        <p>列が見つかりません: {columnId}</p>
      </div>
    );
  }

  // 数値データのみフィルタリング
  const numericData = columnData.filter(
    (value): value is number => typeof value === 'number' && !isNaN(value)
  );

  if (numericData.length === 0) {
    return (
      <div className={styles.errorMessage} style={{ minHeight: `${REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT}px` }}>
        <p>数値データがありません</p>
      </div>
    );
  }

  return (
    <div className="report-qq-plot" style={{ minHeight: `${DEFAULT_SCATTER_HEIGHT}px` }}>
      <div style={{ 
        padding: '12px',
        backgroundColor: styling.backgroundColor,
        borderRadius: '6px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ 
          marginBottom: '8px',
          color: styling.titleColor,
          fontWeight: 'bold'
        }}>
          Q-Q Plot: {columnName}
        </div>
        <div style={{ 
          flex: 1,
          minHeight: 0,
          position: 'relative',
          overflow: 'auto'
        }}>
          <QQPlot
            data={numericData}
            width={600}
            height={DEFAULT_HISTOGRAM_HEIGHT - 60}
            selectedRows={[]}
          />
        </div>
      </div>
    </div>
  );
}

// Graph Builder要素レンダラー
function GraphBuilderElementRenderer({
  element,
  datasets,
  models: _models,
  onElementUpdate,
  height
}: {
  element: GraphBuilderElement;
  datasets: Record<string, DataSet>;
  models?: Record<string, TrainedModel>;
  onElementUpdate?: (elementId: string, updates: Partial<ReportElementUnion>) => void;
  height?: number;
}) {
  const { datasetId, graphConfig } = element;
  const dataset = datasets[datasetId];
  // height が undefined の場合はアスペクト比モード（親コンテナに合わせる）
  // height が指定されている場合は固定高さモード
  const useFlexibleHeight = height === undefined;
  const elementHeight = height ?? REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT;

  // graphConfigからグラフタイプを取得
  const graphType = graphConfig?.type;

  // データセット選択フックを使用
  const selection = useDatasetSelection(datasetId);
  const { selectRows } = useDatasetSelection(datasetId);

  // フィルタ式がある場合は、データセットをフィルタリング
  const filteredDataset = useMemo(() => {
    if (!dataset || !graphConfig?.filterExpression) {
      return dataset;
    }

    try {
      return applyFilterToDataset(dataset, graphConfig.filterExpression);
    } catch (error) {
      logger.warn('[GraphBuilderElementRenderer] Failed to apply filter', { error });
      return dataset; // フィルタ失敗時は元データを使用
    }
  }, [dataset, graphConfig?.filterExpression]);

  const handleTitleChange = (newTitle: string) => {
    if (onElementUpdate) {
      onElementUpdate(element.id, {
        title: newTitle,
        lastModified: new Date().toISOString()
      });
    }
  };

  // グラフ要素からの選択ハンドラー
  const handleElementSelect = (indices: number[], isAdditive: boolean) => {

    if (isAdditive) {
      // 加算選択モード：既存の選択に追加
      const newSelection = [...new Set([...selection.selectedRows, ...indices])];
      selectRows(newSelection);
    } else {
      // 置換選択モード：新しい選択で置き換え
      selectRows(indices);
    }
  };

  // 高さスタイル：アスペクト比モードの場合は100%、固定高さモードはpx指定
  const heightStyle = useFlexibleHeight ? '100%' : `${elementHeight}px`;

  if (!filteredDataset) {
    return (
      <div className={styles.reportElement} style={{ height: heightStyle }}>
        <div className={styles.errorMessage}>
          <p>Dataset not found: {datasetId}</p>
        </div>
      </div>
    );
  }

  // グラフレジストリから適切なレンダラーを取得
  const registryEntry = graphRegistry.get(graphType);
  if (!registryEntry) {
    logger.warn('[GraphBuilderElementRenderer] Unknown graph type:', {
      graphType,
      graphConfigType: graphConfig?.type,
      availableTypes: Array.from(graphRegistry.getAll().keys())
    });
    return (
      <div className={styles.reportElement} style={{ height: heightStyle }}>
        <div className={styles.errorMessage}>
          <p>Unknown graph type: {graphType}</p>
          <p style={{ fontSize: '0.8em', marginTop: '8px' }}>
            Available types: {Array.from(graphRegistry.getAll().keys()).join(', ')}
          </p>
        </div>
      </div>
    );
  }

  // 設定の検証
  const validationError = registryEntry.renderer.validate(graphConfig, filteredDataset);
  if (validationError) {
    return (
      <div className={styles.reportElement} style={{ height: heightStyle }}>
        <div className={styles.errorMessage}>
          <p>{validationError}</p>
        </div>
      </div>
    );
  }

  // グラフをレンダリング（選択機能を有効化）
  const graphElement = registryEntry.renderer.render(graphConfig, filteredDataset, {
    selectedRows: selection.selectedRows,
    onElementSelect: handleElementSelect
  });

  return (
    <div
      className={styles.reportElement}
      style={{ height: heightStyle }}
      data-element-id={element.id}
    >
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        height: '100%',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <EditableTitle
          title={element.title || ''}
          onTitleChange={handleTitleChange}
          placeholder="Click to add graph title..."
          style={{
            margin: '0 0 16px 0',
            color: '#2c3e50',
            fontSize: '16px',
            fontWeight: '600'
          }}
        />
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {graphElement}
        </div>
      </div>
    </div>
  );
}

// Crosstab要素レンダラー
function CrosstabElementRenderer({
  element,
  datasets,
  models: _models,
  onElementUpdate,
  height
}: {
  element: CrosstabElement;
  datasets: Record<string, DataSet>;
  models?: Record<string, TrainedModel>;
  onElementUpdate?: (elementId: string, updates: Partial<ReportElementUnion>) => void;
  height?: number;
}) {
  const { crosstabConfiguration, datasetId } = element;
  const dataset = datasets[datasetId];
  // height が undefined の場合はアスペクト比モード（親コンテナに合わせる）
  const useFlexibleHeight = height === undefined;
  const elementHeight = height ?? REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT;
  const heightStyle = useFlexibleHeight ? '100%' : `${elementHeight}px`;

  const [crosstabResult, setCrosstabResult] = useState<CrosstabDataModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // データセットまたは設定が変更されたら再計算（Live mode）
  useEffect(() => {
    if (!dataset) {
      setError(`Dataset not found: ${datasetId}`);
      setIsLoading(false);
      return;
    }

    if (!hasData(dataset)) {
      setError('Dataset does not have data loaded');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const engine = new CrosstabEngine();
    engine.execute(dataset, crosstabConfiguration)
      .then(result => {
        setCrosstabResult(result);
        setIsLoading(false);
      })
      .catch(err => {
        logger.error('[CrosstabElementRenderer] Error generating crosstab:', err);
        setError(err instanceof Error ? err.message : 'Failed to generate crosstab');
        setIsLoading(false);
      });
  }, [dataset, crosstabConfiguration, datasetId]);

  const handleTitleChange = (newTitle: string) => {
    if (onElementUpdate) {
      onElementUpdate(element.id, {
        title: newTitle,
        lastModified: new Date().toISOString()
      });
    }
  };

  if (!dataset) {
    return (
      <div className={styles.reportElement} style={{ height: heightStyle }}>
        <div className={styles.errorMessage}>
          <p>Dataset not found: {datasetId}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className={styles.reportElement}
        style={{ height: heightStyle }}
        data-element-id={element.id}
      >
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          height: '100%',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <p style={{ color: '#6c757d', fontSize: '14px' }}>Loading crosstab...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.reportElement} style={{ height: heightStyle }}>
        <div className={styles.errorMessage}>
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!crosstabResult) {
    return (
      <div className={styles.reportElement} style={{ height: heightStyle }}>
        <div className={styles.errorMessage}>
          <p>No crosstab result available</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.reportElement}
      style={{ height: heightStyle }}
      data-element-id={element.id}
    >
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        height: '100%',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <EditableTitle
          title={element.title || ''}
          onTitleChange={handleTitleChange}
          placeholder="Click to add crosstab title..."
          style={{
            margin: '0 0 16px 0',
            color: '#2c3e50',
            fontSize: '16px',
            fontWeight: '600'
          }}
        />
        <div style={{
          flex: 1,
          minHeight: 0,
          maxHeight: '80vh',  // 画面の80%を上限に設定
          position: 'relative',
          overflow: 'auto'
        }}>
          <CrosstabPreview
            result={crosstabResult}
            sortConfig={undefined}
            isStale={false}
          />
        </div>
      </div>
    </div>
  );
}